import type React from 'react';
import { useEffect } from 'react';

import { Text } from '@change/ds-core-ui';
import { BaseSlider } from '@change/ds-forms';

import { useI18n } from '@change-corgi/core-react/i18n';

import { usePsfSharePageData } from '#src/app/pages/psf/share/pageContext';
import { useShareGoalCounter } from '#src/app/pages/psf/share/shared/hooks';
import { useCurrentUserId } from '#src/app/shared/hooks/session';

import { ShareFeed } from '../ShareFeed';

import { GoalShareActions } from './components/GoalShareActions';
import { ShareGoalCounter } from './components/ShareGoalCounter';
import { formatGoal, MAX_GOAL, MIN_GOAL } from './constants';
import { useReachGoalStep } from './hooks/useReachGoalStep';
import { useSetGoalStep } from './hooks/useSetGoalStep';
import { useTreatShareGoalCounterExperiment } from './hooks/useTreatShareGoalCounterExperiment';

export const SharingGoalFlow: React.FC<EmptyProps> = () => {
	useTreatShareGoalCounterExperiment();
	const { translate, translatePlural } = useI18n();
	const { data } = usePsfSharePageData();
	const petitionId = data.petition.id;
	const userId = useCurrentUserId();
	const {
		data: { userName, shareFeed },
	} = useReachGoalStep();
	const { goalValue, setGoalValue, trackSliderChange, saveGoal, emoji, message } = useSetGoalStep({
		petitionId,
		userId,
		initialShareGoal: data.initialShareGoal,
	});

	const { isActive: isCounterActive, count } = useShareGoalCounter();

	useEffect(() => {
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}, []);

	const handleShareSuccess = () => {
		// variant_1 has no goal slider, so it has no goal to save.
		if (!isCounterActive) void saveGoal();
	};

	return (
		<>
			<div className="flex flex-col gap-8 px-6 md:gap-9 md:px-10">
				{isCounterActive ? (
					<ShareGoalCounter count={count} />
				) : (
					<>
						<div className="flex flex-col gap-3 text-center">
							<div className="flex flex-col gap-2">
								<Text as="p" fontWeight="bold" className="text-typography-weak m-0">
									{translate('corgi.psf.share.sharing_goal.greeting', { user_name: userName })}
								</Text>
								<Text variant="heading" size="large" as="h1" className="text-typography-strong m-0">
									{translate('corgi.psf.share.sharing_goal.heading')}
								</Text>
							</div>
							<Text variant="body" className="text-typography-strong">
								{translate('corgi.psf.share.sharing_goal.body')}
							</Text>
						</div>

						<div className="mt-2 flex flex-col gap-2 px-4">
							<BaseSlider
								ariaLabel={translate('corgi.psf.share.sharing_goal.slider_aria_label')}
								value={goalValue}
								minValue={MIN_GOAL}
								maxValue={MAX_GOAL}
								step={1}
								onChange={setGoalValue}
								onChangeEnd={(value: number) => {
									trackSliderChange(value);
								}}
								showTooltip
								size="thick"
								tooltipTextSize="medium"
								formatValue={(value: number) =>
									translatePlural('corgi.psf.share.sharing_goal.slider_label', value, { count: formatGoal(value) })
								}
								data-qa="sharing-goal-slider"
								tooltipAlignment="follow"
							/>

							<Text variant="body" size="small" className="text-typography-weak">
								<span aria-hidden>{emoji}</span> {message}
							</Text>
						</div>
					</>
				)}

				<GoalShareActions onShareSuccess={handleShareSuccess} />
			</div>

			{shareFeed.showFeed && (
				<div className="relative mt-7 h-full overflow-hidden md:mt-9" data-qa="share-feed">
					<ShareFeed items={shareFeed.items} />
				</div>
			)}
		</>
	);
};
